import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, Download } from 'lucide-react';
import { invoiceService } from '../services/invoice.service';
import InvoiceTable from '../components/invoice/InvoiceTable';
import StatusUpdateModal from '../components/invoice/StatusUpdateModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { INVOICE_STATUS, INVOICE_TYPE } from '../utils/constants';
import { formatMoney } from '../utils/formatMoney';
import { useDialog } from '../contexts/DialogContext';

const Invoices = () => {
    const { toast, prompt } = useDialog();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [actionLoading, setActionLoading] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [activeTab, setActiveTab] = useState('customer');

    // Vendor invoice state
    const [vendorInvoices, setVendorInvoices] = useState([]);
    const [vendorInvoicesLoading, setVendorInvoicesLoading] = useState(false);
    const [vendorInvoiceStatusFilter, setVendorInvoiceStatusFilter] = useState('');

    useEffect(() => {
        fetchInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, statusFilter, typeFilter]);

    useEffect(() => {
        if (activeTab === 'vendor') fetchVendorInvoiceList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, vendorInvoiceStatusFilter]);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const params = {
                skip: (currentPage - 1) * 100,
                limit: 100,
                // Add filters when backend supports them
                // search: searchTerm,
                // status: statusFilter,
                // invoice_type: typeFilter
            };

            const data = await invoiceService.getInvoices(params);
            setInvoices(data || []);
            // For now, assume we get all invoices in one call
            setTotalPages(1);
        } catch (error) {
            console.error('Error fetching invoices:', error);
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchVendorInvoiceList = async () => {
        setVendorInvoicesLoading(true);
        try {
            const params = { limit: 100 };
            if (vendorInvoiceStatusFilter) params.status = vendorInvoiceStatusFilter;
            const data = await invoiceService.getVendorInvoices(params);
            setVendorInvoices(data || []);
        } catch (error) {
            console.error('Error fetching vendor invoices:', error);
            setVendorInvoices([]);
        } finally {
            setVendorInvoicesLoading(false);
        }
    };

    const handleApproveVendorInvoice = async (id) => {
        setActionLoading(true);
        try {
            await invoiceService.approveVendorInvoice(id);
            fetchVendorInvoiceList();
            toast.success('Vendor invoice approved successfully!');
        } catch (error) {
            console.error('Error approving vendor invoice:', error);
            toast.error('Failed to approve vendor invoice.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRejectVendorInvoice = async (id) => {
        const reason = await prompt({
            title: 'Reject vendor invoice',
            label: 'Rejection reason',
            placeholder: 'Please provide a reason for rejection...',
            required: true,
            multiline: true,
            confirmLabel: 'Reject',
            danger: true,
        });
        if (reason == null || !String(reason).trim()) return;
        setActionLoading(true);
        try {
            await invoiceService.rejectVendorInvoice(id, String(reason).trim());
            fetchVendorInvoiceList();
            toast.success('Vendor invoice rejected.');
        } catch (error) {
            console.error('Error rejecting vendor invoice:', error);
            toast.error('Failed to reject vendor invoice.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDownloadVendorInvoicePdf = async (id) => {
        setActionLoading(true);
        try {
            const response = await invoiceService.downloadVendorInvoicePdf(id);
            if (response.download_url) window.open(response.download_url, '_blank');
        } catch (error) {
            console.error('Error downloading vendor invoice PDF:', error);
            toast.error('Failed to download vendor invoice PDF.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDownload = async (invoice) => {
        setActionLoading(true);
        try {
            await invoiceService.downloadInvoice(invoice.id, invoice.invoice_number);
        } catch (error) {
            console.error('Error downloading invoice:', error);
            toast.error(error.message || 'Failed to download invoice. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDownloadVendorInvoice = async (invoice) => {
        setActionLoading(true);
        try {
            const response = await invoiceService.downloadVendorInvoice(invoice.id);
            // Handle download response - this will return a download URL
            if (response.download_url) {
                // Open the download URL in a new tab
                window.open(response.download_url, '_blank');
            }
        } catch (error) {
            console.error('Error downloading vendor invoice:', error);
            toast.error('Failed to download vendor invoice. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleApprove = async (invoice, notes) => {
        setActionLoading(true);
        try {
            await invoiceService.approveInvoice(invoice.id, notes);
            // Refresh the invoices list
            fetchInvoices();
            toast.success('Invoice approved successfully!');
        } catch (error) {
            console.error('Error approving invoice:', error);
            toast.error('Failed to approve invoice. Please try again.');
            throw error;
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async (invoice, reason, notes) => {
        setActionLoading(true);
        try {
            await invoiceService.rejectInvoice(invoice.id, reason, notes);
            // Refresh the invoices list
            fetchInvoices();
            toast.success('Invoice rejected successfully!');
        } catch (error) {
            console.error('Error rejecting invoice:', error);
            toast.error('Failed to reject invoice. Please try again.');
            throw error;
        } finally {
            setActionLoading(false);
        }
    };

    const handleMarkPaid = async (invoice, paymentMethod, paymentReference, notes) => {
        setActionLoading(true);
        try {
            await invoiceService.updatePaymentStatus(invoice.id, paymentMethod, paymentReference, notes);
            // Refresh the invoices list
            fetchInvoices();
            toast.success('Invoice marked as paid successfully!');
        } catch (error) {
            console.error('Error marking invoice as paid:', error);
            toast.error('Failed to mark invoice as paid. Please try again.');
            throw error;
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateStatus = (invoice) => {
        setSelectedInvoice(invoice);
        setStatusModalOpen(true);
    };

    const closeStatusModal = () => {
        setStatusModalOpen(false);
        setSelectedInvoice(null);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleSearchSubmit = () => {
        setCurrentPage(1);
        fetchInvoices();
    };

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleTypeFilter = (e) => {
        setTypeFilter(e.target.value);
        setCurrentPage(1);
    };

    // Filter invoices based on search term and filters
    const filteredInvoices = invoices.filter(invoice => {
        const matchesSearch = !searchTerm || 
            invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            invoice.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = !statusFilter || 
            invoice.status?.toLowerCase() === statusFilter.toLowerCase();
        
        const matchesType = !typeFilter || 
            invoice.invoice_type?.toLowerCase() === typeFilter.toLowerCase();
        
        return matchesSearch && matchesStatus && matchesType;
    });

    return (
        <div className="space-y-6">
            <div className="mrgall">
                <h1 className="text-2xl font-semibold text-gray-900 mb-4">Invoices Management</h1>

                {/* Tab switcher */}
                <div className="flex gap-2 mb-4">
                    <button
                        onClick={() => setActiveTab('customer')}
                        className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'customer' ? 'bg-blue-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
                    >
                        Customer Invoices
                    </button>
                    <button
                        onClick={() => setActiveTab('vendor')}
                        className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'vendor' ? 'bg-blue-600 text-white' : 'bg-white border text-gray-700 hover:bg-gray-50'}`}
                    >
                        Vendor Invoices
                    </button>
                </div>
            </div>

            {activeTab === 'vendor' ? (
                <div className="p-6 bg-white rounded-lg mrgall border bxshadowhd">
                    <div className="flex gap-4 mb-4 items-center">
                        <select
                            value={vendorInvoiceStatusFilter}
                            onChange={(e) => setVendorInvoiceStatusFilter(e.target.value)}
                            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="processed">Processed</option>
                            <option value="rejected">Rejected</option>
                        </select>
                        <button
                            onClick={fetchVendorInvoiceList}
                            disabled={vendorInvoicesLoading}
                            className="bg-white text-black border px-4 py-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${vendorInvoicesLoading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                    {vendorInvoicesLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {['ID', 'Vendor', 'Service #', 'Rate (ex GST)', 'Fee', 'Extra', 'Total', 'GST', 'Net Payable', 'Status', 'Created', 'Actions'].map(h => (
                                            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {vendorInvoices.length === 0 ? (
                                        <tr><td colSpan={12} className="px-4 py-8 text-center text-gray-500">No vendor invoices found</td></tr>
                                    ) : vendorInvoices.map(vi => (
                                        <tr key={vi.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{vi.id}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700">{vi.vendor_name || vi.vendor_id}</td>
                                            <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                                                {vi.service_number || (vi.service_id != null ? `#${vi.service_id}` : '—')}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {formatMoney(vi.rate_ex_gst ?? vi.rate_amount)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {Number(vi.platform_fee_ex_gst || vi.platform_fee_amount || 0) > 0
                                                    ? `−${formatMoney(vi.platform_fee_ex_gst ?? vi.platform_fee_amount)}${vi.commission_percent != null ? ` (${vi.commission_percent}%)` : ''}`
                                                    : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {Number(vi.extra_charges_amount || vi.extra_ex_gst || 0) > 0
                                                    ? formatMoney(vi.extra_charges_amount ?? vi.extra_ex_gst)
                                                    : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {formatMoney(vi.amount_after_fee_ex_gst ?? vi.gross_ex_gst)}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-700">
                                                {vi.gst_amount != null ? formatMoney(vi.gst_amount) : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">{formatMoney(vi.net_amount)}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                                    vi.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                    vi.status === 'processed' ? 'bg-blue-100 text-blue-800' :
                                                    vi.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                    'bg-yellow-100 text-yellow-800'
                                                }`}>{vi.status}</span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">
                                                {vi.created_at ? new Date(vi.created_at).toLocaleDateString() : '—'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-2">
                                                    {vi.status === 'pending' && (
                                                        <>
                                                        <button
                                                            onClick={() => handleApproveVendorInvoice(vi.id)}
                                                            disabled={actionLoading}
                                                            className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                                        >
                                                            Approve
                                                        </button>
                                                        <button
                                                            onClick={() => handleRejectVendorInvoice(vi.id)}
                                                            disabled={actionLoading}
                                                            className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                                        >
                                                            Reject
                                                        </button>
                                                        </>
                                                    )}
                                                    {vi.pdf_path && (
                                                        <button
                                                            onClick={() => handleDownloadVendorInvoicePdf(vi.id)}
                                                            disabled={actionLoading}
                                                            className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 flex items-center gap-1"
                                                        >
                                                            <Download className="w-3 h-3" /> PDF
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ) : (
            <>
            <div className=" justify-center items-center mrgall">
                <div className="flex gap-4 pb-0 mt-0">

                <h1 className="text-2xl font-semibold text-gray-900">Invoices Management</h1>
                    <div className="flex-1 relative ml-150">
                        <button
                            onClick={handleSearchSubmit}
                            disabled={!searchTerm.trim()}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Search className="w-4 h-4" />
                        </button>
                        <input
                            type="text"
                            placeholder="Search invoices by number or vendor..."
                            value={searchTerm}
                            onChange={handleSearch}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                            className="w-full pl-10 pr-12 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        
                    </div>
                    <select
                        value={typeFilter}
                        onChange={handleTypeFilter}
                        className="drop_bform-black text-black px-4 py-2  rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Types</option>
                        {Object.values(INVOICE_TYPE).map(type => (
                            <option key={type} value={type}>
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                            </option>
                        ))}
                    </select>
                    <select
                        value={statusFilter}
                        onChange={handleStatusFilter}
                        className="drop_bform text-white px-5 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Statuses</option>
                        {Object.values(INVOICE_STATUS).map(status => (
                            <option key={status} value={status}>
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                        ))}
                    </select>
                    
                    <button
                        onClick={fetchInvoices}
                        disabled={loading}
                        className="bg-white text-black border px-4 py-2  border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
                <div className="flex gap-2">
                    {actionLoading && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            Processing...
                        </div>
                    )}
                </div>
            </div>

            <div className=" p-6 bg-white rounded-lg mrgall border bxshadowhd">
               

                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <InvoiceTable
                        invoices={filteredInvoices}
                        onDownload={handleDownload}
                        onDownloadVendorInvoice={handleDownloadVendorInvoice}
                        onUpdateStatus={handleUpdateStatus}
                    />
                )}

                {totalPages > 1 && (
                    <div className="p-4 border-t flex justify-center">
                        <nav className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <span className="px-3 py-1">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                )}
            </div>

            <StatusUpdateModal
                invoice={selectedInvoice}
                isOpen={statusModalOpen}
                onClose={closeStatusModal}
                onApprove={handleApprove}
                onReject={handleReject}
                onMarkPaid={handleMarkPaid}
            />
            </>
            )}
        </div>
    );
};

export default Invoices; 